import { Badge, NativeSelect, cn } from '@provenance/design';
import { PAGE_KINDS, PAGE_KIND_LABEL, type PageKind, type PagePlanEntry } from '@provenance/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';

export interface PagePlanResponse {
  byKind: boolean;
  pages: Array<PagePlanEntry & { label: string; marks: number }>;
  unstampedPages: number[];
}

/** The operator's corrections, one per page, as the API takes them — null when there are none. */
export function pageKindsBody(overrides: Record<number, PageKind>): Array<PageKind | null> | null {
  const pages = Object.keys(overrides).map(Number);
  if (pages.length === 0) return null;
  return Array.from({ length: Math.max(...pages) }, (_, i) => overrides[i + 1] ?? null);
}

/**
 * What each page of the certificate is, before it is issued (T-137, D-028).
 *
 * The server reads every page and decides face, continuation sheet or overleaf; the operator
 * sees why, and can correct a page it got wrong. A page it was unsure about is flagged, never
 * blocking — it is stamped as a continuation sheet unless the operator says otherwise.
 */
export function PageMap({
  documentId,
  overrides,
  onChange,
}: {
  documentId: string;
  overrides: Record<number, PageKind>;
  onChange: (overrides: Record<number, PageKind>) => void;
}) {
  const pageKinds = pageKindsBody(overrides);
  const plan = useQuery({
    queryKey: ['page-plan', documentId, pageKinds],
    queryFn: () => api.post<PagePlanResponse>(`/api/console/documents/${documentId}/page-plan`, { pageKinds }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // A one-page certificate has nothing to decide; a failure here must not get in the way of
  // approving — the server plans again at approval and never refuses over it.
  if (plan.isError) {
    return (
      <p className="flex items-start gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
        <AlertTriangle aria-hidden className="mt-px size-3.5 shrink-0 text-[var(--pv-warn)]" />
        The page map could not be read. Approving still works — pages are worked out again when
        the certificate is stamped.
      </p>
    );
  }
  const data = plan.data;
  if (!data || data.pages.length <= 1) return null;

  const flagged = data.pages.filter((p) => !p.confident);

  return (
    <section aria-busy={plan.isFetching}>
      <h4 className="[font-size:var(--pv-text-h3)] font-bold">Pages</h4>
      <p className="mt-1 [font-size:var(--pv-text-xs)] leading-relaxed text-[var(--pv-muted)]">
        {data.byKind
          ? `This certificate has ${data.pages.length} pages. Each was read and matched to your layout — correct any that are wrong and the preview follows.`
          : `This certificate has ${data.pages.length} pages. Its layout stamps by page number; tag the layout's pages under Stamping to stamp continuation sheets automatically.`}
      </p>

      {flagged.length > 0 && (
        <p className="mt-2 flex items-start gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-clay-deep)]">
          <AlertTriangle aria-hidden className="mt-px size-3.5 shrink-0" />
          <span>
            {flagged.length === 1 ? `Page ${flagged[0]!.page} was` : `Pages ${flagged.map((p) => p.page).join(', ')} were`}{' '}
            not recognised and will be stamped as a continuation sheet. Check{' '}
            {flagged.length === 1 ? 'it' : 'them'} in the preview.
          </span>
        </p>
      )}

      <ul className={cn('mt-3 flex list-none flex-col gap-0 p-0', plan.isFetching && 'opacity-70')}>
        {data.pages.map((page) => (
          <li
            key={page.page}
            className="flex flex-wrap items-center gap-3 border-b border-[var(--pv-line)] py-2 last:border-b-0"
          >
            <span className="pv-mono w-14 shrink-0 [font-size:12px] text-[var(--pv-faint)]">Page {page.page}</span>
            <span className="min-w-0 flex-1">
              {data.byKind ? (
                <NativeSelect
                  value={page.kind}
                  onChange={(e) => onChange({ ...overrides, [page.page]: e.target.value as PageKind })}
                  className="w-auto min-w-[12rem]"
                  aria-label={`What page ${page.page} is`}
                >
                  {PAGE_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {PAGE_KIND_LABEL[kind]}
                    </option>
                  ))}
                </NativeSelect>
              ) : (
                <span className="[font-size:13px]">{page.label}</span>
              )}
              <span className="mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">{page.reason}</span>
            </span>
            {!page.confident && (
              <Badge tone="warn" size="sm">
                Check
              </Badge>
            )}
            {page.source === 'OPERATOR' && (
              <Badge tone="info" size="sm">
                Changed by you
              </Badge>
            )}
            <span className="[font-size:12px] text-[var(--pv-muted)]">
              {page.marks === 0 ? 'No marks' : `${page.marks} mark${page.marks === 1 ? '' : 's'}`}
            </span>
          </li>
        ))}
      </ul>

      {data.unstampedPages.length > 0 && (
        <p className="mt-2 flex items-start gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-clay-deep)]">
          <AlertTriangle aria-hidden className="mt-px size-3.5 shrink-0" />
          <span>
            {data.unstampedPages.length === 1 ? `Page ${data.unstampedPages[0]} gets` : `Pages ${data.unstampedPages.join(', ')} get`}{' '}
            no marks at all. If {data.unstampedPages.length === 1 ? 'it is' : 'they are'} part of the certificate, your
            layout needs marks for that kind of page.
          </span>
        </p>
      )}
    </section>
  );
}
